import React from 'react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '~/components/common/dropdowns/DropdownMenu'
import { Typography } from '~/components/common/Typography'

export interface RichMenuOption<T extends string = string> {
  value: T
  label: string
  description: string
}

interface RichDropdownMenuProps<T extends string = string> {
  /** Options to display in the dropdown */
  options: RichMenuOption<T>[]
  /** Currently selected value */
  selectedValue: T | null
  /** Callback when an option is selected */
  onSelect: (value: T) => void
  /** Custom trigger element */
  trigger: React.ReactNode
  /** Whether the dropdown is open (controlled) */
  open?: boolean
  /** Callback when open state changes (controlled) */
  onOpenChange?: (open: boolean) => void
  /** Alignment of the dropdown content */
  align?: 'start' | 'center' | 'end'
  /** Offset from the trigger */
  sideOffset?: number
  /** Additional className for the content (use min-w-[Xpx] to customize width) */
  contentClassName?: string
  /** Whether the dropdown is disabled */
  disabled?: boolean
}

const CheckIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    width='16'
    height='16'
    viewBox='0 0 16 16'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    className={className}
  >
    <path
      d='M13.3332 4L5.99984 11.3333L2.6665 8'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

export function RichDropdownMenu<T extends string = string>({
  options,
  selectedValue,
  onSelect,
  trigger,
  open,
  onOpenChange,
  align = 'start',
  sideOffset = 8,
  contentClassName = '',
  disabled = false
}: RichDropdownMenuProps<T>) {
  const [internalOpen, setInternalOpen] = React.useState(false)

  const isControlled = open !== undefined
  const isOpen = isControlled ? open : internalOpen

  const handleOpenChange = (newOpen: boolean) => {
    if (disabled) return
    if (isControlled) {
      onOpenChange?.(newOpen)
    } else {
      setInternalOpen(newOpen)
    }
  }

  const handleSelect = (value: T) => {
    onSelect(value)
    handleOpenChange(false)
  }

  return (
    <DropdownMenu open={disabled ? false : isOpen} onOpenChange={handleOpenChange} modal={false}>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        className={`w-[248px] max-w-[248px] p-0 space-y-0 ${contentClassName}`}
        align={align}
        sideOffset={sideOffset}
      >
        {options.map(option => (
          <RichMenuItem
            key={option.value}
            option={option}
            isSelected={selectedValue === option.value}
            onSelect={() => handleSelect(option.value)}
          />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

interface RichMenuItemProps<T extends string = string> {
  option: RichMenuOption<T>
  isSelected: boolean
  onSelect: () => void
}

export function RichMenuItem<T extends string = string>({
  option,
  isSelected,
  onSelect
}: RichMenuItemProps<T>) {
  return (
    <DropdownMenuItem
      onClick={onSelect}
      className={`relative flex w-full h-auto flex-col items-start pl-[12px] pr-[12px] pt-[8px] pb-[16px] rounded-none whitespace-normal overflow-visible text-clip ${isSelected ? 'bg-[rgba(0,0,0,0.04)]' : ''}`}
    >
      <div className='flex items-center justify-between gap-[6px] py-[6px] px-[12px] w-full'>
        <Typography
          fontFamily='geist'
          fontSize='bodyMd'
          fontWeight='medium'
          className={`leading-[22px] break-words whitespace-normal
            ${isSelected ? 'text-tokens-status-link-3' : 'text-[rgba(0,0,0,0.88)]'}
          `}
        >
          {option.label}
        </Typography>
        {isSelected && <CheckIcon className='text-tokens-status-link-3 shrink-0' />}
      </div>
      <div className='px-[12px]'>
        <Typography
          fontFamily='geist'
          fontSize='bodySm'
          fontWeight='medium'
          className='text-tokens-neutral-text-tertiary leading-[16px] break-words whitespace-normal'
        >
          {option.description}
        </Typography>
      </div>
    </DropdownMenuItem>
  )
}

export default RichDropdownMenu
